import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";

export default function ProfilePage() {
  const { user, isAuthenticated, isLoading, logout } = useAuth0();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading your account…
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg font-semibold text-[#5B2E3C]">
          Please login to access your account 🔐
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9FC] px-3 py-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">

        {/* ================= SIDEBAR ================= */}
        <aside className="bg-white rounded-2xl shadow-sm overflow-hidden">

          {/* USER MINI CARD */}
          <div className="p-5 bg-gradient-to-r from-[#FF8FB1] to-[#FF5D8F] text-white">
            <div className="flex items-center gap-3">
              <img
                src={user.picture}
                alt={user.name}
                referrerPolicy="no-referrer"
                className="w-12 h-12 rounded-full border-2 border-white"
              />
              <div>
                <p className="text-xs opacity-90">Hello,</p>
                <p className="font-semibold text-sm leading-tight">
                  {user.name}
                </p>
              </div>
            </div>
          </div>

          {/* NAVIGATION */}
          <nav className="p-3 text-sm space-y-1">

            <NavItem to="/orders" icon="📦" label="My Orders" />
            <NavItem to="/wishlist" icon="❤️" label="My Wishlist" />

            <SectionLabel label="Account Settings" />
            <NavItem to="/profile" icon="👤" label="Profile Information" active />
            <NavItem to="/address" icon="📍" label="Manage Addresses" />

            <SectionLabel label="Payments" />
            <NavItem to="/payments" icon="💳" label="Saved Cards & UPI" />

            <SectionLabel label="More" />
            <NavItem to="/reviews" icon="⭐" label="My Reviews" />

            <button
              onClick={() =>
                logout({
                  logoutParams: { returnTo: window.location.origin },
                })
              }
              className="w-full text-left px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition"
            >
              🚪 Logout
            </button>
          </nav>
        </aside>

        {/* ================= MAIN CONTENT ================= */}
        <main className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">

          {/* PAGE TITLE */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-lg font-semibold text-[#2B2B2B]">
              Personal Information
            </h1>
            <span className="text-xs px-3 py-1 rounded-full bg-green-100 text-green-700">
              Active Account
            </span>
          </div>

          {/* PROFILE FORM */}
          <div className="space-y-6">

            {/* NAME */}
            <Section title="Name">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="First Name" value={user.name?.split(" ")[0]} />
                <Input label="Last Name" value={user.name?.split(" ")[1] || ""} />
              </div>
            </Section>

            {/* EMAIL */}
            <Section title="Email Address">
              <Input value={user.email} />
            </Section>

            {/* LOGIN METHOD */}
            <Section title="Login Method">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FFF0F6] text-sm">
                {getProviderBadge(user.sub)}
              </div>
            </Section>

            {/* SECURITY */}
            <Section title="Account Security">
              <p className="text-sm text-gray-500">
                Your account is protected using secure third-party authentication.
                We never store your password.
              </p>
            </Section>

            {/* DANGER ZONE */}
            <div className="pt-6 border-t">
              <button className="text-sm text-red-600 hover:underline">
                Deactivate Account
              </button>
            </div>

          </div>
        </main>

      </div>
    </div>
  );
}

/* ================= COMPONENTS ================= */

function NavItem({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition ${
        active
          ? "bg-[#FFE3EE] text-[#D14670]"
          : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </Link>
  );
}

function SectionLabel({ label }) {
  return (
    <p className="px-3 pt-3 pb-1 text-xs uppercase tracking-wide text-gray-400">
      {label}
    </p>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-[#6E3C51] mb-2">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Input({ label, value }) {
  return (
    <div>
      {label && (
        <label className="block text-xs text-gray-500 mb-1">
          {label}
        </label>
      )}
      <input
        value={value}
        readOnly
        className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm"
      />
    </div>
  );
}

function getProviderBadge(sub) {
  if (!sub) return "Email Login ✉️";
  if (sub.startsWith("google")) return "Google Account 🟢";
  if (sub.startsWith("github")) return "GitHub Account ⚫";
  return "Email Login ✉️";
}
